import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RequestStatus } from '../../../config/store';
import useMembers from '../../../hooks/useMembers';
import { RootState } from '../../../store/rootStore';
import useLicenses from '../../subscriptions/hooks/useLicenses';
import { getUsers, getUsersProjectCount } from '../store/users.store';
import { UserData } from '../types/user';

const useUsers = (
  only?: string[]
): {
  users: UserData[];
  usersWithLicense: UserData[];
  usersWithoutLicense: UserData[];
  isLoading: boolean;
} => {
  const dispatch = useDispatch<any>();
  const lastFailedTokenRef = React.useRef<string | undefined>(undefined);
  const requestedIdsRef = React.useRef<Set<string>>(new Set());
  const { licenses, isLoading: licenseIsLoading } = useLicenses();
  const { uniqueUserIds, members, token } = useMembers();
  const fetchedUsers = useSelector((state: RootState) => state.users.users.data || {});
  const projectCounts = useSelector((state: RootState) => state.users.userProjectCounts.data || []);
  const userStatus = useSelector((state: RootState) => state.users.users.request.get.status);
  const isLoading = React.useMemo(
    () => [RequestStatus.Pending, RequestStatus.Unsent].includes(userStatus),
    [userStatus]
  );
  React.useEffect(() => {
    if (!token) {
      return;
    }

    const notFetchedUsers = uniqueUserIds
      .filter((id) => {
        if (only && !only.includes(id)) {
          return false;
        }
        return !fetchedUsers[id] && !requestedIdsRef.current.has(id);
      })
      .filter((i) => !!i);
    if (!notFetchedUsers.length || userStatus === RequestStatus.Pending) {
      return;
    }

    if (userStatus === RequestStatus.Failure && lastFailedTokenRef.current === token) {
      return;
    }

    notFetchedUsers.forEach((id) => requestedIdsRef.current.add(id));
    dispatch(getUsers({ userIds: notFetchedUsers, token }));
    dispatch(getUsersProjectCount({ userIds: notFetchedUsers, token }));
    lastFailedTokenRef.current = userStatus === RequestStatus.Failure ? token : undefined;
  }, [uniqueUserIds, fetchedUsers, only, userStatus, dispatch, token]);
  const users = React.useMemo(() => {
    const safeFetchedUsers = Object.values(fetchedUsers as Record<string, any>).filter(
      (user: any) => !!user && !!user.id
    ) as any[];

    return safeFetchedUsers
      .map((user: any) => {
        const projectCount = projectCounts.find((c: any) => c?.user_id === user.id);
        const license = licenses.find((license: any) => license?.assigned_to === user.id);
        return {
          ...user,
          license,
          memberships: members.filter((m: any) => m?.user_id === user.id),
          projectCount: projectCount && projectCount.projects_count,
          full_name: `${user.first_name || ''}${user.name ? ` ${user.name}` : ''}`,
        };
      })
      .filter((user) => user.memberships.length) as UserData[];
  }, [fetchedUsers, projectCounts, licenses, members]);

  const { usersWithLicense, usersWithoutLicense } = React.useMemo(
    () => ({
      usersWithLicense: users.filter((u: UserData) => !!u.license),
      usersWithoutLicense: users.filter((u: UserData) => !u.license),
    }),
    [users]
  );
  return { users, isLoading: isLoading || licenseIsLoading, usersWithLicense, usersWithoutLicense };
};

export default useUsers;
